import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

// 通用异步搜索下拉：支持按编码 / 中西语名称关键字检索（走后端 list 的 q 参数），
// 300ms 防抖，点击外部关闭。用于报价单/合同的「客户」「物料」「来源合同」等选择器。
// 传入 paginated=true 时启用「加载更多」按 offset 翻页，避免一次性展开超大结果集。
export interface SearchSelectProps<T extends { id: string }> {
  endpoint: string;
  value: string | null;
  // 已选项的展示标签（父组件持有，便于回填/详情态显示）
  label: string;
  onChange: (row: T | null) => void;
  // 把一行转成下拉里的主文本
  optionTitle: (row: T) => string;
  // 可选副文本（编码 / 税号 / 规格等）
  optionMeta?: (row: T) => ReactNode;
  placeholder?: string;
  params?: Record<string, string | number>;
  limit?: number;
  // 开启后按 offset 增量加载（footer 出现「加载更多」）。默认关闭＝单次一页。
  paginated?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  ariaLabel?: string;
}

export function SearchSelect<T extends { id: string }>({
  endpoint, value, label, onChange, optionTitle, optionMeta, placeholder, params,
  limit = 20, paginated = false, disabled, clearable = true, ariaLabel,
}: SearchSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  const fetchPage = async (baseOffset: number, append: boolean) => {
    const my = ++seq.current;
    const q = query.trim();
    if (append) setLoadingMore(true); else { setLoading(true); setError(null); }
    try {
      const data = await api.list<T>(endpoint, {
        q, limit, ...(baseOffset ? { offset: baseOffset } : {}), ...(params || {}),
      });
      if (my !== seq.current) return;
      setRows((prev) => {
        if (!append) return data;
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...data.filter((r) => !seen.has(r.id))];
      });
      setHasMore(paginated && data.length === limit);
      setOffset(baseOffset + data.length);
    } catch (e) {
      if (my !== seq.current) return;
      setError(e instanceof ApiError ? e.message : '检索失败');
      if (!append) setRows([]);
    } finally {
      if (my === seq.current) { setLoading(false); setLoadingMore(false); }
    }
  };

  // 输入 / 展开变化时回到第一页重查；query 为空展示初始候选。
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { void fetchPage(0, false); }, query.trim() ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, endpoint]);

  // 点击外部关闭。
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (row: T) => {
    onChange(row);
    setOpen(false);
    setQuery('');
  };
  const clear = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="ssel" ref={wrapRef}>
      <div className="ssel-control">
        <input
          className="txt"
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          value={open ? query : label}
          placeholder={open ? (placeholder || '输入编码 / 名称检索…') : (label || placeholder || '点击选择…')}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (!disabled) setOpen(true); }}
        />
        {clearable && value && !disabled ? (
          <button type="button" className="ssel-clear" onClick={clear} aria-label="清除选择" tabIndex={-1}>×</button>
        ) : null}
      </div>
      {open ? (
        <div className="ssel-pop" role="listbox">
          {loading ? <div className="ssel-note">检索中…</div> : null}
          {error ? <div className="ssel-note bad">{error}</div> : null}
          {!loading && !error && rows.length === 0 ? <div className="ssel-note">无匹配记录，换个关键字试试。</div> : null}
          {rows.map((row) => (
            <button
              type="button"
              key={row.id}
              className={`ssel-opt${row.id === value ? ' on' : ''}`}
              onClick={() => choose(row)}
            >
              <span className="t">{optionTitle(row)}</span>
              {optionMeta ? <span className="m">{optionMeta(row)}</span> : null}
            </button>
          ))}
          {paginated && hasMore && !loading ? (
            <button
              type="button"
              className="ssel-more"
              disabled={loadingMore}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void fetchPage(offset, true)}
            >
              {loadingMore ? '加载中…' : '加载更多 ↓'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
